import React, { useContext, useMemo } from 'react'
import { useShopPayButtons } from '../../../hooks/useShopPayButtons'
import {
    ApiContentSection,
    ApiShopperLandingPageLink,
    ApiSectionType,
    isCartActionButtonType,
    LP_BUTTON_TYPE,
    parseCombinedAtcBuyNowButtonText,
    selectVariantWithFallback,
} from '@storefronts/common-types'
import { SectionContainer } from './Shared'
import { PageContext } from '../../../context/PageContextProvider'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { CartContext } from '../../../context/cartContext'
import { useLandingPageLinksForHashes } from '../../../hooks/useLandingPageLinksForHashes'
import { resolveCartTrackingVariant } from '../../../utils/userEventTools'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import {
    CustomizationContext,
    SECONDARY_CTA_BORDER_VAR,
} from '../../../context/CustomizationContextProvider'
import { LockIcon } from '../../icons/Icons'
import { CTAButton } from '../../shared/CTAButton'
import { ShopPayCTAButton } from '../../shared/ShopPayCTAButton'
import { ScrollingLayout } from '../../../style/scrollingLayout'

type ButtonSectionType = Extract<
    ApiContentSection,
    { type: ApiSectionType.CTA_BUTTON }
>

export const ButtonSection: React.FC<{ section: ButtonSectionType }> = ({
    section,
}) => {
    const {
        setIsGridExpanded,
        setActiveLPLink,
        selectedCartActionVariantShopifyId,
        setIsCartOpen,
    } = useContext(PageContext)
    const { fetchAndCacheLinksForHashes } = useContext(LandingPageLinksContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { addCartItem } = useContext(CartContext)
    const {
        primaryCtaTextColor,
        secondaryCtaFillColor,
        secondaryCtaTextColor,
    } = useContext(CustomizationContext)
    const displayShopPayButton = useShopPayButtons()
    const { landingPage } = useContext(ViddyLPContext)
    const landingPageLinks = landingPage.landingPageLinks ?? []

    const resolvedLinks = useLandingPageLinksForHashes(
        section.linkHash ? [section.linkHash] : [],
    )
    const lpLink = resolvedLinks[0]

    const selectedVariant = useMemo(() => {
        if (!isCartActionButtonType(section.actionType)) return null
        if (!lpLink?.variantInfo?.length) return null
        return selectVariantWithFallback({
            variants: lpLink.variantInfo,
            selectedVariantId: selectedCartActionVariantShopifyId,
            defaultVariantId: lpLink.renderedDefaultVariantId,
        })
    }, [lpLink, selectedCartActionVariantShopifyId, section.actionType])

    const getOrFetchLink = async (): Promise<
        ApiShopperLandingPageLink | undefined
    > => {
        if (lpLink) return lpLink
        if (!section.linkHash) return undefined
        const fetched = await fetchAndCacheLinksForHashes([section.linkHash])
        return fetched[0]
    }

    const getTrackingVariantForCart = async () => {
        const link = await getOrFetchLink()
        if (!link) return undefined
        return resolveCartTrackingVariant(
            link,
            selectedCartActionVariantShopifyId,
        )
    }

    const handleAddToCartAction = async () => {
        const trackingVariant = await getTrackingVariantForCart()
        if (!trackingVariant) return
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'email_vlp_primary',
            location: 'inline',
        })
        addCartItem(trackingVariant, 1)
        setIsCartOpen(true)
    }

    const handleBuyNowAction = async (shopPay?: boolean) => {
        const trackingVariant = await getTrackingVariantForCart()
        if (!trackingVariant) return
        Analytics.TRACK_CART_CHECKOUT_CLICK({
            buyNowVariant: trackingVariant,
        })
        addCartItem(trackingVariant, 1, null, true, shopPay)
    }

    const handleClick = async () => {
        switch (section.actionType) {
            case LP_BUTTON_TYPE.LINK:
                if (section.url) window.open(section.url, '_blank')
                break
            case LP_BUTTON_TYPE.PDP: {
                const link = await getOrFetchLink()
                if (link) setActiveLPLink(link)
                break
            }
            case LP_BUTTON_TYPE.PLP:
                if (landingPageLinks.length > 1) {
                    setIsGridExpanded(true)
                    setActiveLPLink(null)
                } else if (landingPageLinks.length === 1) {
                    setIsGridExpanded(false)
                    setActiveLPLink(landingPageLinks[0])
                } else {
                    setIsGridExpanded(true)
                    setActiveLPLink(null)
                }
                break
            case LP_BUTTON_TYPE.BUY_NOW:
                await handleBuyNowAction()
                break
            case LP_BUTTON_TYPE.ADD_TO_CART:
                await handleAddToCartAction()
                break
            case LP_BUTTON_TYPE.NONE:
            default:
                break
        }
    }

    if (section.actionType === LP_BUTTON_TYPE.NONE) return null

    const isCartAction = isCartActionButtonType(section.actionType)
    const disabled = isCartAction && !selectedVariant?.available

    const style = section.style || {}

    if (section.actionType === LP_BUTTON_TYPE.ADD_TO_CART_AND_BUY_NOW) {
        const { addToCart: addToCartLabel, buyNow: buyNowLabelText } =
            parseCombinedAtcBuyNowButtonText(section.buttonText)
        const buyNowLabel = (
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon color={style.textColor || primaryCtaTextColor} />
                {buyNowLabelText}
            </span>
        )
        return (
            <SectionContainer maxContentWidth={ScrollingLayout.CTA_MAX_WIDTH}>
                <div
                    style={{
                        display: 'flex',
                        gap: 16,
                        width: '100%',
                        alignItems: 'stretch',
                    }}
                >
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        <CTAButton
                            backgroundColor={secondaryCtaFillColor}
                            textColor={secondaryCtaTextColor}
                            border={`var(${SECONDARY_CTA_BORDER_VAR})`}
                            buttonText={addToCartLabel}
                            onClick={handleAddToCartAction}
                            disabled={disabled}
                            padding="13px 0px"
                            loader={false}
                        />
                    </div>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        {displayShopPayButton && !disabled ? (
                            <ShopPayCTAButton
                                onClick={() => handleBuyNowAction(true)}
                                disabled={disabled}
                                loader
                            />
                        ) : (
                            <CTAButton
                                backgroundColor={style.backgroundColor}
                                textColor={style.textColor}
                                buttonText={buyNowLabel}
                                fitLabel={buyNowLabelText}
                                onClick={() => handleBuyNowAction()}
                                disabled={disabled}
                                padding="13px 0px"
                                loader
                            />
                        )}
                    </div>
                </div>
            </SectionContainer>
        )
    }

    return (
        <SectionContainer maxContentWidth={ScrollingLayout.CTA_MAX_WIDTH}>
            <CTAButton
                backgroundColor={style.backgroundColor}
                textColor={style.textColor}
                buttonText={section.buttonText}
                onClick={handleClick}
                disabled={disabled}
                padding="13px 0px"
                loader={section.actionType === LP_BUTTON_TYPE.BUY_NOW}
            />
        </SectionContainer>
    )
}
